"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/Card";
import { TrendingUp, Clock, ShieldCheck, Sparkles } from "lucide-react";

export const TalentCalculator: React.FC = () => {
  const [roles, setRoles] = React.useState<number>(10);
  const [agencyFee, setAgencyFee] = React.useState<number>(15);

  const AVG_ANNUAL_SALARY = 600000; // ₹6 Lakhs
  const DAYS_SAVED_PER_ROLE = 28; // 30 days to 2 days

  const totalAgencyFees = roles * AVG_ANNUAL_SALARY * (agencyFee / 100);
  const talentFlowCost = 12000 * 12; // ₹1.44 Lakhs annual flat license
  const netSavings = Math.max(0, totalAgencyFees - talentFlowCost);
  const daysSaved = roles * DAYS_SAVED_PER_ROLE;

  return (
    <Card className="w-full relative overflow-hidden" glowColor="rgba(6, 182, 212, 0.12)">
      {/* Background radial gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(6,182,212,0.05),transparent_60%)] pointer-events-none" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center p-6 sm:p-8">
        
        {/* Sliders Input Block */}
        <div className="lg:col-span-7 flex flex-col gap-8">
          <div>
            <span className="text-[10px] uppercase font-bold tracking-widest text-primary bg-primary/5 dark:bg-primary/10 px-2.5 py-1 rounded-md">
              ROI Calculator
            </span>
            <h3 className="font-heading font-extrabold text-2xl text-foreground mt-3">
              Calculate your recruitment savings.
            </h3>
            <p className="text-xs text-text-muted mt-2">
              Adjust the sliders below to estimate the cost and time savings of replacing traditional agency contractors with TalentFlow AI.
            </p>
          </div>

          {/* Slider 1: Open Roles */}
          <div className="flex flex-col gap-3">
            <div className="flex justify-between items-center text-xs font-bold">
              <span className="text-foreground">Open Roles to Fill (Annual)</span>
              <span className="text-primary font-extrabold text-sm">{roles} Positions</span>
            </div>
            <input
              type="range"
              min="1"
              max="50"
              value={roles}
              onChange={(e) => setRoles(Number(e.target.value))}
              className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between text-[10px] text-text-muted">
              <span>1 Role</span>
              <span>50 Roles</span>
            </div>
          </div>

          {/* Slider 2: Agency Fee */}
          <div className="flex flex-col gap-3">
            <div className="flex justify-between items-center text-xs font-bold">
              <span className="text-foreground">Traditional Recruiter Fee (%)</span>
              <span className="text-accent font-extrabold text-sm">{agencyFee}% of Salary</span>
            </div>
            <input
              type="range"
              min="10"
              max="30"
              value={agencyFee}
              onChange={(e) => setAgencyFee(Number(e.target.value))}
              className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <div className="flex justify-between text-[10px] text-text-muted">
              <span>10% Fee</span>
              <span>30% Fee</span>
            </div>
          </div>
        </div>

        {/* ROI Metrics Display Block */}
        <div className="lg:col-span-5 bg-slate-50 dark:bg-slate-950 p-6 sm:p-8 rounded-xl border border-border-muted/50 flex flex-col gap-6 justify-between h-full relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full blur-2xl pointer-events-none" />

          {/* Net Savings Metric */}
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-success tracking-wider mb-2">
              <TrendingUp className="h-4 w-4" />
              Net Annual Savings
            </div>
            <div className="text-3xl sm:text-4xl font-heading font-extrabold text-success tracking-tight">
              ₹{netSavings.toLocaleString("en-IN")}
            </div>
            <p className="text-[10px] text-text-muted mt-1">
              After deducting TalentFlow AI annual flat license fee (₹1.44 Lakhs).
            </p>
          </div>

          {/* Time Saved Metric */}
          <div className="border-t border-border-muted/50 pt-4">
            <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-primary dark:text-accent tracking-wider mb-2">
              <Clock className="h-4 w-4" />
              Time Saved Sourcing
            </div>
            <div className="text-2xl font-heading font-extrabold text-foreground">
              {daysSaved} Days
            </div>
            <p className="text-[10px] text-text-muted mt-1">
              Time to fill slashed from average 30 days to 2 days per role.
            </p>
          </div>

          {/* Trust Guarantees */}
          <div className="border-t border-border-muted/50 pt-4 flex flex-col gap-2">
            <div className="flex items-center gap-2 text-[10px] font-bold text-foreground">
              <ShieldCheck className="h-4 w-4 text-accent shrink-0" />
              <span>100% Employer-Funded Compliance</span>
            </div>
            <div className="flex items-center gap-2 text-[10px] font-bold text-foreground">
              <Sparkles className="h-4 w-4 text-accent shrink-0" />
              <span>Full Access to Pre-Vetted Talent Pool</span>
            </div>
          </div>

        </div>

      </div>
    </Card>
  );
};
